<template>
    <div>
        <fe-dialog
            title="Create smartFORM"
            @close="$emit('dismiss')"
            :width="selectingStudents ? 700 : 400"
            @accept="doAccept"
            @dismiss="$emit('dismiss')"
            dismissButtonText="Cancel"
            :acceptButtonText="selectingStudents ? 'Continue' : 'Save'"
            :acceptButtonDisabled="selectingStudents ? studentSelectDisabled : buttonDisabled"
            disableAutoclose
            persistent
        >
            <v-form @submit.prevent>
                <v-layout column v-show="!selectingStudents">
                    <v-flex>
                        <fe-remote-combo
                            autofocus
                            label="Form Template"
                            :items="filteredFormTemplates"
                            :sort="formSort"
                            v-model="record.form"
                        />
                    </v-flex>

                    <v-flex v-if="studentsRequired && !$props.studentId">
                        <fe-label>Select Student</fe-label>
                        <v-autocomplete flat solo dense
                            v-model="searchStudent"
                            :search-input="searchText"
                            @update:search-input="searchText = $event"
                            style="margin-top: 7px;"
                            :items="searchResults"
                            itemText="student_full_name"
                            itemValue="student_id"
                            return-object
                            clearable
                            placeholder="Quick Add (3 characters minimum)"
                            :no-data-text="noSearchString ? '' : 'No data available'"
                            :hide-no-data="noSearchString"
                        >
                            <template #append>
                                <span class="student-selector" @click="selectingStudents=true">Advanced</span>
                            </template>
                        </v-autocomplete>
                    </v-flex>

                    <div v-if="selectedStudents.length > 0">
                        <div :style="{color: selectedStudents.length > selectionLimit ? 'red' : undefined }">
                            {{selectedStudents.length}} Students
                        </div>
                        <div style="max-height: 200px; overflow-y: scroll;" class="pa-1 mb-3">
                            <div v-for="(s, i) in selectedStudents" :key="`student-`+i" class="d-flex student-row">
                                <div class="pt-1">{{ s.student_full_name }}</div>
                                <div class="ml-auto">
                                    <v-btn class="ma-0" icon small @click="selectedStudents.splice(i, 1)">
                                        <v-icon style="font-size: 14px;">fas fa-times</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- <v-flex v-if="studentsRequired" class="text-right">
                        <div @click="selectingStudents=true" class="mb-2 student-selector">Students Selected: {{ selectedStudents.length }}</div>
                    </v-flex> -->
                    <v-flex>
                        <v-label>Form Name</v-label>
                        <v-text-field
                            :rules="$fieldValidators('text', null, {required: true, limit: 100})"
                            counter=100
                            flat solo dense
                            v-model="record.name"
                            :disabled="customFormName !== null"
                        />
                        <div v-if="customFormName !== null" style="color: #9297A6; font-size: 12px;">
                            This is an auto-generated form name designed from the form template.
                            <div v-if="selectedStudents.length > 1"> Each form instance name will reflect the individual student attached to the instance.</div>
                        </div>
                    </v-flex>

                    <v-flex>
                        <fe-remote-combo
                            label="Folder (Optional)"
                            rootProperty="folders"
                            :url="$models.getUrl('formFolder', 'read')"
                            v-model="record.folder"
                        />
                    </v-flex>
                </v-layout>

                <div v-show="selectingStudents">
                    <student-search v-model="advancedSelectedStudents"/>
                    <div class="caption text-right ma-1" :style="{color: advancedSelectedStudents.length > selectionLimit ? 'red': null}">
                        {{advancedSelectedStudents.length}}/{{selectionLimit}}
                    </div>
                </div>
            </v-form>
        </fe-dialog>

        <fe-crud ref="formCrud" :config="formConfig" autoload @read="v => formTemplates = v" />
        <fe-crud ref="permissionsCrud" :config="permissionConfig" autoload @read="v => userPermissions = v" />
    </div>
</template>

<script>
    import lodash from 'lodash'
    import { mapState } from 'vuex'
    import StudentSearch from '@/components/common/StudentSearch'
    export default {
        name: 'CreateSmartForm',
        components: {
            StudentSearch
        },
        props: ['value', 'studentId', 'collectionDashboardId', 'interventionId', 'interventionPlanId'],
        data() {
            return {
                saving: false,
                searchStudent: null,
                searchText: '',
                searchResults: [],
                record: {
                    folder: null,
                    form: null,
                    name: ''
                },
                selectingStudents: false,
                selectedStudents: [],
                advancedSelectedStudents: [],
                selectionLimit: 100,
                noSearchString: true,
                formTemplates: [],
                userPermissions: {},
                customFormName: null,
            }
        },
        computed: {
            ...mapState('global', ['sessionUser']),
            formConfig() {
                return this.$_.cloneDeep(this.$models.form)
            },
            permissionConfig() {
                let cfg = this.$_.cloneDeep(this.$models.formPermissions)
                cfg.read.params.user_id = this.sessionUser.user.id
                return cfg
            },
            studentsRequired() {
                let r = this.record

                return (r && r.form && r.form.requires_student && !this.collectionDashboardId)
            },
            studentSelectDisabled() {
                let count = this.advancedSelectedStudents.length
                return  count === 0 || count > this.selectionLimit ? true : false
            },
            buttonDisabled() {
                if (this.saving) {
                    return true
                } else if ((!this.record.name && !this.customFormName) || !this.record.form || !this.record.form.id) {
                    return true
                } else if (this.studentsRequired && this.selectedStudents.length == 0) {
                    return true
                }

                return false
            },
            hasBaseValues() {
                return this.record.form && this.record.form.id && this.record.name
            },
            filteredFormTemplates() {
                if (this.sessionUser.security_codes.includes('MANAGE_ALL_FORM_TEMPLATES') || !Object.keys(this.userPermissions).length) {
                    return this.formTemplates
                }

                let create = new Set()
                Object.keys(this.userPermissions[this.sessionUser.user.id]).forEach(key => {
                    if(this.userPermissions[this.sessionUser.user.id][key].can_create) {
                        create.add(parseInt(key))
                    }
                })
                
                return this.formTemplates.filter(form => create.has(form.id))
            },
        },
        watch: {
            searchText: lodash.debounce(function(v) {
                if (v && v.length >= 3) {
                    this.noSearchString = false
                    this.$modelGet('studentGradeHistory', {
                        school_year_id: this.$store.state.global.shareableStores.current_year.id,
                        name: v,
                    }).then(response => {
                        this.searchResults = response
                    })
                } else {
                    this.noSearchString = true
                }
            }, 500),
            searchStudent(v) {
                if (v) {
                    let f = this.selectedStudents.find(r => v.student_id === r.student_id)

                    if (f) return
                    this.selectedStudents.push(v)
                    this.searchStudent = null
                    this.searchText    = null
                }
            },
            selectingStudents(v) {
                if (!v) {
                    this.selectedStudents =  this.$_.uniqBy(this.selectedStudents.concat(this.advancedSelectedStudents), 'student_id')
                }
            },
            'record.form'(v) {
                if(v && this.selectedStudents.length) {
                    this.$axios.get('formResponse.php?action=get&property=prefab_name&form_id=' + v.id + '&student_id=' + this.selectedStudents[0].student_id)
                         .then((resp) => {
                             if (resp.data.success) {
                                 this.customFormName = resp.data.name
                                 if(this.selectedStudents.length === 1) this.record.name = resp.data.name
                             }
                         })
                }
            },
            selectedStudents(v) {
                if(v.length && this.record.form) {
                    this.$axios.get('formResponse.php?action=get&property=prefab_name&form_id=' + this.record.form.id + '&student_id=' + v[0].student_id)
                        .then((resp) => {
                            if (resp.data.success) {
                                this.customFormName = resp.data.name
                                if(this.selectedStudents.length === 1) this.record.name = resp.data.name
                            }
                        })
                }
            }
        },
        mounted() {
            if(this.studentId) {
                let id = [].concat(this.studentId)
                this.$axios.get(`students.php?action=get&property=student&id=${id.join(',')}`)
                .then(r => {
                    this.selectedStudents.push(
                        ...r.data.map(s => ({
                            student_id: s.id,
                            student_full_name: [s.lname,s.fname].join(', '),
                        }))
                    )
                })
            }
        },
        methods: {
            formSort(a,b) {
                if(a.name.toLowerCase() < b.name.toLowerCase()) return -1
                if(a.name.toLowerCase() > b.name.toLowerCase()) return 1
                return 0
            },
            doAccept() {
                if (this.selectingStudents) {
                    this.selectingStudents = false
                    return
                }
                let r = {}

                this.$setLoading(true)
                this.saving = true
                const formId = this.record?.form?.id ?? null
                if(this.collectionDashboardId) {
                    r = {
                        form_id: formId,
                        name: this.record.name,
                        dashboard_id: this.collectionDashboardId ? this.collectionDashboardId : null
                    }
                    this.$axios.post('dashboard.php?action=create&property=form_instances&form_id=' + r.form_id + '&dashboard_id=' + r.dashboard_id + '&name=' + r.name)
                        .then(response => {
                            let r = this.$ecResponse(response)
                            this.record = {
                                folder: null,
                                form: null,
                                name: ''
                            }
                            this.$emit('created', r)
                            this.$emit('dismiss')
                        })
                        .finally(() => {
                            this.$setLoading(false)
                            this.saving = false
                        })
                } else {
                    r = {
                        form_instances: [{
                            folder_id: this.record.folder ? this.record.folder.id : null,
                            form_id: formId,
                            name: this.record.name,
                            student_id: this.$_.map(this.selectedStudents, 'student_id'),
                            intervention_plan_id: this.interventionPlanId
                        }]
                    }
                    this.$axios.post(this.$models.getUrl('formInstance', 'create'), r)
                        .then(response => {
                            let r = this.$ecResponse(response)
                            this.record = {
                                folder: null,
                                form: null,
                                name: ''
                            }
                            this.$emit('created', r)
                            this.$emit('dismiss')
                        })
                        .finally(() => {
                            this.$setLoading(false)
                            this.saving = false
                        })
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
.student-selector {
    font-weight: bold;
    cursor: pointer;
    text-decoration: underline;
    color: rgb(0, 108, 144);
}

.student-row {
    padding-right: 4px;
    padding-left: 8px;
    &:hover {
        background: var(--fe-selected);
        font-weight: bold;
    }
}
</style>
